import axios from "axios";
import { Navigate } from "react-router-dom";
import baseUrl, { apiBaseUrl } from "../Api";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const API_TIMEOUT = 5 * 60 * 1000; // 5 minutes

const apiUrl = process.env.REACT_APP_API_PROXY || "https://esonline.edusports.in";

// Student fitness APIs that should use SVO when available_dates has data
const SVO_FITNESS_ENDPOINTS = {
  fittest_kids_list: "sah",
  fittest_kids_list_top_10: "sah",
  fittest_kids_list_bottom_10: "sah",
  genderwise_fitness_trends: "sah",
  scl_vs_nsnl_avg: "sah-api",
  scl_vs_state_avg: "sah-api",
};

function fetchWithTimeout(url, options = {}, timeout = API_TIMEOUT) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  if (options.signal) {
    options.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  const { signal: _, ...restOptions } = options;
  return fetch(url, { ...restOptions, signal: controller.signal }).finally(() =>
    clearTimeout(timer)
  );
}

function parseDataReq(data) {
  const raw = String(data || "").trim();
  const parts = raw.split("&");
  const dataReq = (parts[0] || "").trim();
  let chid = "";
  for (let i = 1; i < parts.length; i++) {
    const [key, ...rest] = parts[i].split("=");
    if ((key || "").trim() === "chid") {
      chid = rest.join("=").trim();
    }
  }
  return { dataReq, chid };
}

function sanitizeFitnessData(data) {
  if (!Array.isArray(data)) return [];
  // SAH sometimes returns sparse arrays like [null, null, { report_name: ... }, null]
  return data
    .filter((item) => item != null && typeof item === "object")
    .map((item) => {
      const next = { ...item };
      // Fitness trends: std_wise_array can be null
      if (next.std_wise_array == null) next.std_wise_array = [];
      if (!Array.isArray(next.std_wise_array)) next.std_wise_array = [];
      if (next.girls_fitness_trends_average_overall == null) {
        next.girls_fitness_trends_average_overall = [];
      }
      if (next.boys_fitness_trends_average_overall == null) {
        next.boys_fitness_trends_average_overall = [];
      }
      if (next.fitness_trends_avg_overall == null) {
        next.fitness_trends_avg_overall = [];
      }
      return next;
    });
}

function hasUsableFitnessItem(item) {
  if (!item || typeof item !== "object") return false;

  const stdArr = Array.isArray(item.std_wise_array) ? item.std_wise_array : [];
  if (
    stdArr.some(
      (s) =>
        (Array.isArray(s?.skl_vs_nat_avg) && s.skl_vs_nat_avg.length > 0) ||
        (Array.isArray(s?.skl_vs_state_avg) && s.skl_vs_state_avg.length > 0) ||
        (Array.isArray(s?.girls_fitness_trends_average) &&
          s.girls_fitness_trends_average.length > 0) ||
        (Array.isArray(s?.boys_fitness_trends_average) &&
          s.boys_fitness_trends_average.length > 0) ||
        (Array.isArray(s?.fitness_trends_avg) &&
          s.fitness_trends_avg.length > 0)
    )
  ) {
    return true;
  }

  const overallKeys = [
    "girls_fitness_trends_average_overall",
    "boys_fitness_trends_average_overall",
    "fitness_trends_avg_overall",
    "skl_vs_nat_avg_overall",
    "skl_vs_state_avg_overall",
  ];
  if (
    overallKeys.some((key) =>
      (Array.isArray(item[key]) ? item[key] : []).some(
        (x) =>
          x != null &&
          (typeof x !== "object" ||
            x.scl_avg_score != null ||
            x.nat_avg_score != null ||
            x.state_avg_score != null ||
            (x?.score_avg ?? x?.old_avg ?? x?.latest_avg ?? 0) !== 0)
      )
    )
  ) {
    return true;
  }

  if (
    (Array.isArray(item.overall_fq_male) && item.overall_fq_male.length > 0) ||
    (Array.isArray(item.overall_fq_female) && item.overall_fq_female.length > 0)
  ) {
    return true;
  }

  // Other fitness payloads (school vs national/state, etc.)
  return Object.keys(item).some(
    (k) =>
      k !== "report_name" &&
      k !== "std_wise_array" &&
      item[k] != null &&
      !(Array.isArray(item[k]) && item[k].length === 0)
  );
}

function isEmptyFitnessResponse(res) {
  if (!res) return true;
  if (res.status === false) return true;
  const data = sanitizeFitnessData(res.data);
  if (data.length === 0) return true;
  // e.g. fitness trends shells with std_wise_array: null and no overall scores
  return !data.some(hasUsableFitnessItem);
}

function normalizeSvoResponse(payload, { suppressToast = false } = {}) {
  if (!payload || typeof payload !== "object") {
    return { status: false, data: [], message: "Invalid response" };
  }

  const statusCode = payload.statusCode ?? payload.code;
  const isError =
    Boolean(payload.error) ||
    (typeof statusCode === "number" && statusCode >= 400) ||
    payload.status === false;

  if (isError) {
    // e.g. { statusCode: 400, error: "Bad Request", message: "State information is not avilable" }
    const message =
      payload.message || payload.error || "Unable to fetch fitness data";
    if (!suppressToast) toast.error(message);
    return { status: false, data: [], message, code: statusCode || 400 };
  }

  // Existing SAH/esonline shape: { status: true/false, data: [...] }
  if (typeof payload.status === "boolean") {
    return {
      status: payload.status,
      message: payload.message || "",
      code: payload.code || statusCode || 200,
      data: sanitizeFitnessData(payload.data),
    };
  }

  const data = payload.data ?? payload.result ?? null;
  if (!Array.isArray(data)) {
    const message = payload.message || "No fitness data available";
    if (message && !suppressToast) toast.error(message);
    return {
      status: false,
      data: [],
      message,
      code: statusCode || 400,
    };
  }

  return {
    status: true,
    data: sanitizeFitnessData(data),
    message: payload.message || "Success",
    code: statusCode || 200,
  };
}

function hasAvailableDates(payload) {
  if (!payload) return false;
  if (Array.isArray(payload)) return payload.length > 0;
  if (typeof payload === "object") {
    const data = payload.data;
    if (Array.isArray(data)) return data.length > 0;
    if (data) return true;
  }
  return false;
}

async function checkAvailableDates(chid, svoToken) {
  const headers = {};
  if (svoToken) {
    headers.Authorization = `Bearer ${svoToken}`;
  }
  const url = `${apiBaseUrl}/sah?data_req=available_dates&chid=${encodeURIComponent(
    chid
  )}`;
  const response = await fetchWithTimeout(url, { headers });
  if (!response.ok) return false;
  const json = await response.json();
  return hasAvailableDates(json);
}

async function fetchFromSvo(dataReq, chid, svoToken, options = {}) {
  const endpoint = SVO_FITNESS_ENDPOINTS[dataReq];
  if (!endpoint || !chid) return null;

  const headers = {};
  if (svoToken) {
    headers.Authorization = `Bearer ${svoToken}`;
  }

  const url = `${apiBaseUrl}/${endpoint}?data_req=${encodeURIComponent(
    dataReq
  )}&chid=${encodeURIComponent(chid)}`;

  const response = await fetchWithTimeout(url, { headers });
  const json = await response.json();
  return normalizeSvoResponse(json, options);
}

async function fetchFromEsoOnline(data, esoToken, options = {}) {
  return fetchWithTimeout(
    `${apiUrl}/esonline-int/retrieve_data_via_api/api/v1/stdnt_asmnt_data/?data_req=${data}&token=${esoToken}`,
    options
  )
    .then((response) => response.json())
    .then((response) => {
      if (response && typeof response === "object" && Array.isArray(response.data)) {
        return { ...response, data: sanitizeFitnessData(response.data) };
      }
      return response;
    })
    .catch((error) => {
      if (error?.name === "AbortError") {
        if (options?.signal?.aborted) {
          return { status: false, data: [], message: "Request aborted" };
        }
        toast.error("Request timed out. Please try again.");
        return { status: false, data: [], message: "Request timed out" };
      }
      return catcherror(error);
    });
}

/**
 * Student fitness data loader.
 * - If available_date exists → call SAH (apiBaseUrl)
 * - If SAH response is empty / failed → fall back to esonline (old_token)
 * - Else → esonline flow
 *
 * Token from v2/generate-token-for-assessment:
 * - token → JWT when available_date exists (SAH)
 * - old_token → esonline hash token
 */
export async function sahassessmentdatelist(data, toke) {
  try {
    const tokenPayload =
      typeof toke === "object" && toke !== null ? toke : { token: toke };
    const responseToken =
      tokenPayload.token || tokenPayload.data?.token || "";
    const oldToken =
      tokenPayload.old_token ||
      tokenPayload.data?.old_token ||
      "";
    const availableDate =
      tokenPayload.available_date ||
      tokenPayload.data?.available_date ||
      null;

    // SAH uses JWT (token when available_date is set)
    const svoToken =
      tokenPayload.svo_token ||
      tokenPayload.data?.svo_token ||
      (availableDate ? responseToken : null) ||
      (typeof responseToken === "string" && responseToken.startsWith("eyJ")
        ? responseToken
        : null);

    // esonline always uses old_token (hash); fall back to token if it is not a JWT
    const esoToken =
      oldToken ||
      (typeof responseToken === "string" &&
      responseToken &&
      !responseToken.startsWith("eyJ")
        ? responseToken
        : "") ||
      (typeof toke === "string" ? toke : "");

    const { dataReq, chid } = parseDataReq(data);
    const isFitnessSvoApi = Boolean(SVO_FITNESS_ENDPOINTS[dataReq]);

    if (isFitnessSvoApi && chid && availableDate) {
      const esoAbortController = new AbortController();
      try {
        const svoPromise = fetchFromSvo(dataReq, chid, svoToken, {
          suppressToast: true,
        }).catch((e) => {
          console.error("SVO fitness API failed, falling back to esonline", e);
          return null;
        });

        const esoPromise = fetchFromEsoOnline(data, esoToken, {
          signal: esoAbortController.signal,
        }).catch((e) => {
          if (e?.name !== "AbortError") {
            console.error("esonline API failed", e);
          }
          return null;
        });

        const svoRes = await svoPromise;

        // If SVO has data, cancel esonline and return SVO immediately
        if (!isEmptyFitnessResponse(svoRes)) {
          try {
            esoAbortController.abort();
          } catch (_) {}
          return svoRes;
        }

        // Otherwise return esonline response
        return await esoPromise;
      } catch (e) {
        console.error("Parallel fetch failed, falling back to esonline", e);
      }
    }

    return fetchFromEsoOnline(data, esoToken);
  } catch (error) {
    catcherror(error);
  }
}

export async function schoolteamplayeryesorno(id) {
  try {
    return await axios
      .get(`${baseUrl}/school-head-user-list/${id}`, { timeout: API_TIMEOUT })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        catcherror(error);
      });
  } catch (error) {
    catcherror(error);
  }
}

function catcherror(error) {
  console.log(error);
  if (error?.code === "ECONNABORTED" || error?.name === "AbortError") {
    toast.error("Request timed out. Please try again.");
  } else if (error?.response?.status === 401) {
    localStorage.clear();
    <Navigate to={"/login"} />;
  } else if (error?.response?.status === 422) {
    Swal.fire({
      title:
        "There are few errors occured while inserting/updating the data to the database.",
      showCancelButton: false,
      confirmButtonColor: "#d71635",
      confirmButtonText: "Ok",
      allowOutsideClick: false,
    });
  } else if (error?.response?.status === 0) {
    toast.error(error.message);
  } else if (error?.response?.status === 500) {
    toast.error(error.response.statusText);
  } else if (
    error?.response?.status !== 200 &&
    error?.response?.data?.status === false &&
    error.response.data.code !== 200
  ) {
    toast.error(error.response.data.message);
  } else if (error.request) {
    toast.error(error.request.statusText);
  } else {
    toast.error(error.message);
  }
}
